import { Component, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapMarker } from '@angular/google-maps';
import { MapThemeDirective } from 'src/app/directives/map-theme.directive';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { jsPDF } from "jspdf";
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExportProgress } from 'src/app/interfaces/shared/export-progress';
// import { GalleryComponent } from 'src/app/components/gallery/gallery.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { addMinutes, isBefore } from 'date-fns';
import { domToCanvas } from 'modern-screenshot';
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { groupRisksByImage, iconNameForTitle, RiskImage } from 'src/app/classes/risks/risk-group';
import { PDFTool } from 'src/app/classes/shared/pdf.tool';
import { Pop } from 'src/app/classes/shared/pop';
import * as Tool from 'src/app/classes/shared/threeDTools';
import { normalizeModel } from 'src/app/classes/shared/tools';
import { ActionDialogComponent } from 'src/app/components/project-pages/actions/dialogs/action-dialog/action-dialog.component';
import { LinkActionDialogComponent } from 'src/app/components/project-pages/actions/dialogs/link-action-dialog/link-action-dialog.component';
import { RiskDetailPanelComponent } from 'src/app/components/project-pages/risks/risk-detail-panel/risk-detail-panel.component';
import { RiskReportComponent } from 'src/app/components/project-pages/risks/risk-report/risk-report.component';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { Action } from 'src/app/interfaces/api/action';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Metric, Risk } from 'src/app/interfaces/api/metric';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';
import { ActionService } from 'src/app/services/api/actions/action.service';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ModelService } from 'src/app/services/api/models/model.service';
import { SpinnerComponent } from 'ui-library';

interface OverlayRiskVM {
  number: number;
  metricId: string;
  iconRed: string;
  iconOrange: string;
  dx: number;
  dy: number;
}

interface OverlayImageVM {
  attachmentId: string;
  lat: number;
  lon: number;
  count: number;
  x: number;
  y: number;
  pastilleRed: string;
  pastilleOrange: string;
  risks: OverlayRiskVM[];
}

@Component({
  selector: 'app-map-mode',
  imports: [
    TranslateModule,
    CommonModule,
    MatIconModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatChipsModule,
    SpinnerComponent,
    GoogleMapsModule,
    MapThemeDirective,
    RiskDetailPanelComponent
  ],
  standalone: true,
  templateUrl: './map-mode.component.html',
  styleUrls: ['./map-mode.component.scss']
})
export class MapModeComponent implements OnInit, OnChanges, OnDestroy {
  canvasElement: HTMLCanvasElement | null = null;
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild('maps', { static: false }) maps!: any;
  @Input() project!: Project;
  @Input() user!: User;
  @Input() modelId!: string;
  @Input() loading: boolean = false;
  @Input() startDate: string = "";
  @Input() endDate!: string;
  @Input() risksObs!: Observable<any[]>;
  @Input() riskFiltersObs!: Observable<any>;
  @Output() onExport: EventEmitter<ExportProgress> = new EventEmitter();
  @Output() onLoaded: EventEmitter<boolean> = new EventEmitter();

  mapOptions: any;
  model: any = {};
  helpText: string = "Les actions suggérées ici sont remplies par un expert chaque jour. Elles sont susceptibles d'être améliorées par vos retours terrains.";
  currentRisk!: Metric | null;
  imageAngle: number = 0;
  destroy$ = new Subject<void>();
  mapWidth = 600;
  mapHeight = 600;
  groundOverlay: any;
  pdf = new jsPDF('l', 'px', 'a4');
  markers: any = {};
  userLastPosition: any = [];
  viewerPositions = [];
  mapObject: any = {};
  showMap = true;
  projectXY = [0, 0];
  colors = Config.colors;
  icons: any;
  risks: any[] = [];
  riskImages: RiskImage[] = [];
  overlayImages: OverlayImageVM[] = [];
  expandedImageId: string | null = null;
  capturing: boolean = false;
  markerVisibility: any = {};
  mapLoaded: boolean = false;
  attachments: Record<string, any> = {}
  actionsByRisk: Record<string, { id: string; title: string }[]> = {};
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      risk: { ai: ['dev'], manual: ALL_ROLES }
    }
  };
  projection?: google.maps.MapCanvasProjection;

  constructor(
    private metricsService: MetricService,
    private dialog: MatDialog,
    private attachmentsService: AttachmentService,
    private pop: Pop,
    private translate: TranslateService,
    private modelsService: ModelService,
    private galleryTools: GalleryTool,
    private pdfTool: PDFTool,
    private actionService: ActionService,
    private ngZone: NgZone,
  ) { }

  @HostListener('window: resize', ['$event'])
  onWindowResize(event: Event) {
    this.calculateMapSize();
  }

  async ngOnInit() {
    this.mapLoaded = false;
    this.removeMarkers();
    this.markerVisibility = {};

    if (!this.modelId) return;

    await this.loadIcons();
    this.getModel();

    this.risksObs.pipe(takeUntil(this.destroy$)).subscribe(async risks => {
      this.risks = risks.filter(m => m.payload.lat && m.payload.lon);
      this.risks.forEach(risk => risk.payload["displayTitle"] = this.galleryTools.getDisplayName(risk.payload["title"]));
      this.mapLoaded = false;
      this.markerVisibility = {};
      await this.setMarkers();

      if (!this.risks.length)
        this.currentRisk = null;

      this.risks.sort((a, b) => isBefore(a.createdDate, b.createdDate) ? 1 : -1);
      this.risks.forEach(m => this.markerVisibility[m.id] = false);
    });

    this.riskFiltersObs.pipe(takeUntil(this.destroy$)).subscribe((riskFilters: any) => {
      if (!this.risks.length) return;

      setTimeout(async () => {
        this.currentRisk = null;
        let { selectedUnits, sequenceList, zoneList, riskList } = riskFilters;

        this.risks.forEach(m => {
          if (selectedUnits.length && !selectedUnits.includes(m.unit)) {
            return this.setMarkerVisibility(m.id, false)
          }

          let risk = riskList.find((l: any) => l.key == m.payload.title);

          if (risk && !risk.value) {
            return this.setMarkerVisibility(m.id, false)
          }

          let sequence = sequenceList.find((l: any) => l.key == m.payload.sequence);

          if (sequence && !sequence.value) {
            return this.setMarkerVisibility(m.id, false)
          }

          let zone = zoneList.find((l: any) => l.key == m.payload.zone);

          if (zone && !zone.value) {
            return this.setMarkerVisibility(m.id, false)
          }

          return this.setMarkerVisibility(m.id, true)
        })

        if (!Object.keys(this.markers).length)
          await this.setMarkers();

        const visibleRisks = this.risks.filter(m => this.markerVisibility[m.id]);
        await this.getAttachments(visibleRisks.filter(m => !m.gallery));
        this.riskImages = groupRisksByImage(visibleRisks, this.attachments);
        this.expandedImageId = null;
        this.buildOverlay();
        this.updateNativeMarkerVisibility();

        let riskIndex = this.risks.findIndex(m => {
          if (this.markerVisibility[m.id]) return true;
          return false;
        })

        this.currentRisk = riskIndex >= 0 ? this.risks[riskIndex] : null;

        if (this.currentRisk)
          this.onMarkerClick(this.currentRisk);

        if (!this.mapLoaded) {
          setTimeout(() => {
            this.mapLoaded = true;
            this.onLoaded.emit(this.mapLoaded);
          }, 2000);
        }
      }, 500);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modelId']?.currentValue) {
      this.ngOnDestroy();
      this.ngOnInit();
    }
    if (changes['project']?.currentValue) {
      this.loadActionsByRisk();
    }
  }

  private loadActionsByRisk() {
    if (!this.project?.id) return;
    this.actionService.getActions({ project: this.project.id }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (actions: Action[]) => {
        const map: Record<string, { id: string; title: string }[]> = {};
        (actions || []).forEach(a => {
          const ids = ((a.risks as any[]) || []).map(r =>
            r && typeof r === 'object' && 'id' in r ? r.id : r
          );
          const legacyId = !ids.length && (a as any).risk
            ? (typeof (a as any).risk === 'object' ? (a as any).risk.id : (a as any).risk)
            : null;
          const targets = ids.length ? ids : (legacyId ? [legacyId] : []);
          targets.forEach((rid: string) => {
            if (!rid) return;
            (map[rid] ||= []).push({ id: a.id, title: a.title });
          });
        });
        this.actionsByRisk = map;
      },
      error: () => { this.actionsByRisk = {}; },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeMarkers();
  }

  onCommentEdit() {
    if (this.currentRisk) {
      const { createdDate, _id, ...putMetric } = this.currentRisk;
      this.metricsService.setMetric(putMetric).subscribe(res => {
      })
    }
  }

  onShowImage() {
    try {
      if (!this.currentRisk) return;

      if (this.currentRisk.payload?.attachment) {
        this.openGallery(this.currentRisk.payload.attachment);
      } else {
        this.attachmentsService.getAttachments({ createdDate: this.currentRisk.createdDate }).subscribe(attachments => {
          if (!this.currentRisk) return;

          if (attachments && attachments[0]) {
            if (!this.currentRisk.payload.attachment)
              this.currentRisk.payload.attachment = attachments[0].id;

            this.openGallery(attachments[0].id);
          } else {
            this.pop.toast(this.translate.instant("risks.image_not_found"), "danger", 4000, "search_off");
          }
        })
      }
    } catch (err: any) {
      console.error("Error while loading gallery :>>", err.toString());
    }
  }

  linkAction() {
    if (!this.currentRisk) return;
    const ref = this.dialog.open(LinkActionDialogComponent, {
      width: '640px',
      data: {
        riskId: this.currentRisk.id,
        riskTitle: this.currentRisk.payload['displayTitle'] || this.currentRisk.payload.title,
        projectId: this.project?.id,
      },
    });
    ref.afterClosed().subscribe(res => {
      if (res) this.loadActionsByRisk();
    });
  }

  openAction(actionId: string) {
    this.actionService.getAction(actionId).pipe(takeUntil(this.destroy$)).subscribe(action => {
      if (!action) return;
      const ref = this.dialog.open(ActionDialogComponent, {
        width: '720px',
        data: { mode: 'edit', action: { ...action }, projectId: this.project?.id },
      });
      ref.afterClosed().subscribe(res => { if (res) this.loadActionsByRisk(); });
    });
  }

  reportRisk() {
    const dialogRef = this.dialog.open(RiskReportComponent, {
      width: "600px",
      data: {
        risk: this.currentRisk
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (this.currentRisk && res.ok && res.comment) {
        this.currentRisk.payload.comment = res.comment;
        this.currentRisk.payload["status"] = res.status;

        this.metricsService.reportRisk(this.currentRisk).subscribe(result => {
          this.pop.toast(this.translate.instant("risks.report_save"), "success", 4000, "save");
        })
      }
    })
  }

  async openGallery(attachmentId: string) {
    let index = -1;

    const getImageInfo = (dir: string) => {
      return new Promise((resolve, reject) => {
        let risks = this.risks.filter(m => this.markerVisibility[m.id]);

        if (dir == "next") {
          index--;
        } else if (dir == "prev") {
          index++;
        }

        if (dir == "") {
          index = risks.findIndex(m => m.id == this.currentRisk?.id);
        }

        if (index <= -1)
          index = 0;

        if (index > risks.length - 1)
          index = risks.length - 1;

        attachmentId = risks[index].payload.attachment;
        this.currentRisk = risks[index];

        if (this.attachments[attachmentId] && this.currentRisk) {
          this.onMarkerClick(this.currentRisk);
          resolve(this.getImageInfoData(this.attachments[attachmentId], getImageInfo));
        } else
          this.attachmentsService.getAttachment(attachmentId).subscribe(attachment => {
            if (attachment && this.currentRisk) {
              this.attachments[attachmentId] = attachment;
              this.onMarkerClick(this.currentRisk);
              resolve(this.getImageInfoData(attachment, getImageInfo))
            } else {
              this.pop.toast(this.translate.instant("risks.image_not_found"), "danger", 4000, "search_off");
              reject("no image found");
            }
          });
      })
    }

    const dialogRef = this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: await getImageInfo("")
    });

    dialogRef.afterClosed().subscribe((res) => {
    });
  }

  getImageInfoData(attach: Attachment, getImageInfo: Function) {
    return {
      index: 0,
      id: attach.id,
      name: attach.name,
      tracker: attach.tracker,
      project: this.project,
      urls: [attach.url],
      tags: attach.tags,
      startDate: attach.createdDate,
      endDate: addMinutes(attach.createdDate, 1).toISOString(), // ...
      processed: attach.processed,
      user: this.user,
      customVisionIds: attach.customVisionIds ?? {},
      type: "Attachments",
      boxes: attach.predictions,
      display: ["risk"],
      onUpdate: new EventEmitter(),
      tagDisplayConfig: this.tagDisplayConfig,
      getImageInfo
    }
  }

  loadMap(object: any) {
    this.mapLoaded = false;
    this.onLoaded.emit(false);
    const lat = object.mapCenter?.lat;
    const lng = object.mapCenter?.lon;

    let mapOptions: google.maps.MapOptions = {
      center: { lat, lng },
      zoom: this.mapOptions?.zoom || (this.userLastPosition.length != 0 ? this.userLastPosition[0].zoom : 18),
      tilt: this.userLastPosition.length != 0 ? this.userLastPosition[0].tilt : 0,
      mapTypeId: google.maps.MapTypeId.SATELLITE
    }

    this.mapOptions = mapOptions;

    if (this.model.origin.mapAngle > 0) {
      this.rotationImage(this.model.origin.mapAngle);
    } else {
      this.updateMap(object);
    }

    this.map.mapDblclick.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      const streetView = this.map.getStreetView();

      streetView.setOptions({
        position: { lat: data.latLng.lat(), lng: data.latLng.lng() },
        pov: { heading: 70, pitch: -10 },
      });

      streetView.setVisible(true);
    })

    if (this.map.googleMap) {
      this.map.googleMap.setOptions({ zoomControl: object.origin.zoomEnabled, gestureHandling: object.origin.panEnabled ? 'greedy' : 'none' });
      const overlay = new google.maps.OverlayView();
      overlay.onAdd = () => { };
      overlay.draw = () => {
        this.projection = overlay.getProjection();
        this.ngZone.run(() => this.updateOverlayPositions());
      };
      overlay.setMap(this.map.googleMap);

      setTimeout(() => {
        this.projection = overlay.getProjection();
        this.updateOverlayPositions();
      }, 1000)
    }
  }

  calculateMapSize() {
    if (this.maps?.nativeElement) {
      const { width, height } = this.maps.nativeElement.getBoundingClientRect();
      this.mapHeight = height;
      this.mapWidth = width;
    }
  }

  async updateMap(object: any) {
    if (this.groundOverlay) {
      this.groundOverlay.setMap(null);
    }

    const imageUrl = object.mapImage ?? object.mapImageSrc ?? null;

    this.groundOverlay = new google.maps.GroundOverlay(
      imageUrl,
      object.mapCoord,
      { clickable: false }
    );

    this.groundOverlay.setMap(this.map.googleMap);
  }

  removeMarkers() {
    Object.keys(this.markers).forEach(k => {
      this.markers[k].setMap(null);
      this.markerVisibility[k] = false;
    });

    this.markers = {};
  }


  async getCanvas() {
    try {
      return await domToCanvas(this.maps.nativeElement)
    } catch (e) {
      console.error(e)
      return null;
    }
  }

  getMarkerPos(metric: Metric) {
    if (!this.projection) return { x: -10, y: -10 };

    let ids = Object.keys(this.markers);

    for (let id of ids) {
      if (id == metric.id) {
        let pos: number[] = [];
        let marker = this.markers[id];
        const position = marker.getPosition();

        if (!position)
          return { x: -10, y: -10 };

        const point = this.projection.fromLatLngToContainerPixel(position);

        return {
          x: Math.round(point!.x),
          y: Math.round(point!.y),
        };
      }
    }

    return { x: -10, y: -10 };
  }

  get currentImage(): RiskImage | null {
    if (!this.currentRisk) return null;
    return this.riskImages.find(img => img.risks.some(r => r.metric.id === this.currentRisk!.id)) ?? null;
  }

  get selectedNumber(): number {
    const img = this.currentImage;
    if (!img) return 1;
    return img.risks.find(r => r.metric.id === this.currentRisk!.id)?.number ?? 1;
  }

  onSelectNumber(n: number) {
    const image = this.currentImage;
    const entry = image?.risks.find(r => r.number === n);
    if (!entry) return;
    this.onMarkerClick(entry.metric);
    // Keep the map fan open on the selected image so its icon can be highlighted (RG-06)
    if (image && image.risks.length > 1) this.expandedImageId = image.attachmentId;
  }

  onMarkerClick(metric: Metric) {
    this.currentRisk = metric;

    Object.keys(this.markers).forEach(k => {
      if (this.currentRisk && k == this.currentRisk.id) {
        this.markers[k].setZIndex(9);
        this.markers[k].setIcon(metric.iconHover);
      } else {
        this.markers[k].setZIndex(0);
        let _metric = this.risks.find(m => m.id == k);

        if (_metric)
          this.markers[k].setIcon(_metric.icon);
      }
    })
  }

  getModel() {
    this.modelsService.getModel(this.modelId).pipe(takeUntil(this.destroy$)).subscribe(async results => {
      try {
        this.model = results;

        const normalized = normalizeModel(results);
        let object = {
          mapCenter: normalized.mapCenter,
          mapCoord: normalized.mapCoord,
          origin: normalized.origin,
          mapImage: normalized.mapImage ?? normalized.mapImageSrc ?? ""
        };

        this.userLastPosition = this.viewerPositions.filter((t: any) => t.projectId == this.project.id);
        this.mapObject = object;
        this.projectXY = Tool.getXYFromLatLonAlt2(object.origin?.position?.lat, object.origin?.position?.lon, 0)
        this.loadMap(this.mapObject);
      } catch (e) {
        console.error("Error: ", e)
      }
    }, error => {
      this.showMap = false;
      console.error(error)
    });
  }

  async setMarkers() {
    this.removeMarkers();

    this.risks.forEach(async m => {
      m.color = this.colors.danger;
      m.visible = false;

      const iconName = this.iconNameForTitle(m.payload.title);
      const svg = this.buildSvg(iconName, this.colors.danger, true);
      const svgHover = this.buildSvg(iconName, this.colors['warning'], true);

      if (!svg || !svgHover) {
        return;
      }

      m.icon = await this.loadImageAsPNG('data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg), 40, 40)
      m.iconHover = await this.loadImageAsPNG('data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgHover), 40, 40)

      let marker = new google.maps.Marker({
        position: new google.maps.LatLng(m.payload.lat, m.payload.lon),
        map: this.map.googleMap,
        title: m.payload.title,
        zIndex: 0,
        opacity: 1,
        visible: this.markerVisibility[m.id] || false,
        icon: m.icon
      });

      marker.addListener("click", (marker: MapMarker) => {
        this.ngZone.run(() => {
          this.expandedImageId = null;
          this.onMarkerClick(m);
        });
      });

      this.markers[m.id] = marker;
    });
  }

  iconNameForTitle(title: string): string {
    return iconNameForTitle(title);
  }

  // Build a colored, sized risk SVG string. When arrow=false the marker arrow
  // (the polygon tagged `marker-arrow`) is stripped for the deployed fan icons.
  buildSvg(iconName: string, color: string, arrow: boolean): string | null {
    let raw = this.icons?.[iconName];
    if (!raw) return null;
    if (!arrow) raw = raw.replace(/<polygon\b[^>]*class="[^"]*marker-arrow[^"]*"[^>]*\/>/g, '');
    let svg = raw.replaceAll('{{ color }}', color).replace('{{ iconColor }}', '#FFF');
    svg = svg.replace(/{{ size }}/g, '50').replace(/{{ scale }}/g, '1');
    return svg;
  }

  private svgDataUri(iconName: string, color: string): string {
    let svg = this.buildSvg(iconName, color, false);
    if (!svg) return '';
    // Fan icons drop the arrow, so tighten the viewBox to the circle's bounding box.
    // The circles sit at cx=22 (danger at cx=25) in a 50-wide box, so centering the raw
    // image would leave each icon off-centre by a different amount and skew the fan spacing.
    svg = this.tightenViewBoxToCircle(svg);
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  }

  private tightenViewBoxToCircle(svg: string): string {
    const m = svg.match(/<circle[^>]*\bcx="([\d.]+)"[^>]*\bcy="([\d.]+)"[^>]*\br="([\d.]+)"/);
    if (!m) return svg;
    const cx = parseFloat(m[1]!), cy = parseFloat(m[2]!), r = parseFloat(m[3]!);
    return svg.replace(/viewBox="[^"]*"/, `viewBox="${cx - r} ${cy - r} ${2 * r} ${2 * r}"`);
  }

  // Build the count pastille SVG (dynamic color + number, keeps the marker arrow).
  buildNumberSvg(color: string, count: number, arrow: boolean): string | null {
    let raw = this.icons?.['number'];
    if (!raw) return null;
    if (!arrow) raw = raw.replace(/<polygon\b[^>]*class="[^"]*marker-arrow[^"]*"[^>]*\/>/g, '');
    let svg = raw.replaceAll('{{ color }}', color)
      .replaceAll('{{ iconColor }}', '#FFF')
      .replaceAll('{{ number }}', String(count));
    svg = svg.replace(/{{ size }}/g, '50').replace(/{{ scale }}/g, '1');
    return svg;
  }

  private numberDataUri(color: string, count: number): string {
    const svg = this.buildNumberSvg(color, count, true);
    return svg ? 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg) : '';
  }

  // Build the HTML overlay model for multi-risk images (pastille + deployed fan).
  buildOverlay() {
    const red = this.colors.danger;
    const orange = this.colors['warning'];

    this.overlayImages = this.riskImages
      .filter(img => img.risks.length > 1 && !!img.risks[0]?.metric.payload.lat && !!img.risks[0]?.metric.payload.lon)
      .map(img => {
        const anchor = img.risks[0]!.metric.payload;
        const n = img.risks.length;
        const radius = 46;
        // Comfortable fixed spacing; the arc grows past a half-circle (wrapping to the sides
        // and lower half) as needed, keeping ~110° free at the bottom for the pin's arrow.
        let stepDeg = 50;
        const maxSpan = 300;
        if ((n - 1) * stepDeg > maxSpan) stepDeg = maxSpan / (n - 1);
        const spanDeg = (n - 1) * stepDeg;
        const startDeg = -90 - spanDeg / 2;

        const risks: OverlayRiskVM[] = img.risks.map((r, i) => {
          const iconName = this.iconNameForTitle(r.metric.payload.title || '');
          const angle = (startDeg + i * stepDeg) * Math.PI / 180;
          return {
            number: r.number,
            metricId: r.metric.id,
            iconRed: this.svgDataUri(iconName, red),
            iconOrange: this.svgDataUri(iconName, orange),
            dx: Math.round(Math.cos(angle) * radius),
            dy: Math.round(Math.sin(angle) * radius),
          };
        });

        return {
          attachmentId: img.attachmentId,
          lat: anchor.lat!, lon: anchor.lon!,
          count: n, x: -9999, y: -9999,
          pastilleRed: this.numberDataUri(red, n),
          pastilleOrange: this.numberDataUri(orange, n),
          risks,
        };
      });

    this.updateOverlayPositions();
  }

  updateOverlayPositions() {
    if (!this.projection) return;
    for (const vm of this.overlayImages) {
      const point = this.projection.fromLatLngToContainerPixel(new google.maps.LatLng(vm.lat, vm.lon));
      vm.x = point ? Math.round(point.x) : -9999;
      vm.y = point ? Math.round(point.y) : -9999;
    }
  }

  // Native per-metric markers stay for PDF export; hide the ones belonging to a
  // multi-risk image (the overlay owns their display). RG-01 single-risk unchanged.
  updateNativeMarkerVisibility() {
    const multiMetricIds = new Set<string>();
    this.riskImages.filter(img => img.risks.length > 1)
      .forEach(img => img.risks.forEach(r => multiMetricIds.add(r.metric.id)));

    Object.keys(this.markers).forEach(id => {
      const show = !!this.markerVisibility[id] && !multiMetricIds.has(id);
      this.markers[id].setVisible(show);
    });
  }

  onPastilleClick(vm: OverlayImageVM) {
    if (this.expandedImageId === vm.attachmentId) {
      this.expandedImageId = null;
      return;
    }
    this.expandedImageId = vm.attachmentId;
    // RG-03: default the panel to the first risk. onMarkerClick also resets the other native
    // markers back to red, so a previously-selected single risk isn't left orange.
    const first = this.riskImages.find(i => i.attachmentId === vm.attachmentId)?.risks[0]?.metric;
    if (first) this.onMarkerClick(first);
  }

  onFanClick(vm: OverlayImageVM, r: OverlayRiskVM) {
    const entry = this.riskImages.find(i => i.attachmentId === vm.attachmentId)?.risks.find(x => x.metric.id === r.metricId);
    if (entry) this.onMarkerClick(entry.metric);
  }

  loadIcons() {
    return new Promise((resolve, reject) => {
      try {
        var icons = ['danger', 'hauteur', 'plain-pied', 'effondrement', 'levage', 'coactivite', 'ensevelissement', 'posture', 'number'];
        var loadCount = 0;

        if (!this.icons) {
          this.icons = {};

          icons.forEach((icon) => {
            let xhr, url;
            url = `/models/risks/SVG/${icon}.svg`;
            xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);

            xhr.onload = async () => {
              this.icons[icon] = xhr.responseText;
              loadCount++;

              if (loadCount == icons.length)
                resolve(true)
            }

            xhr.send(null);
          })
        } else {
          resolve(true)
        }
      } catch (e) {
        console.error(e)
      }
    })
  }

  loadImageAsPNG(url: string, height: number, width: number) {
    return new Promise((resolve, reject) => {
      let sourceImage = new Image();

      sourceImage.onload = () => {
        let png = new Image();
        let cnv = document.createElement('canvas'); // doesn't actually create an element until it's appended to a parent,
        // so will be discarded once this function has done it's job
        cnv.height = height;
        cnv.width = width;
        let ctx = cnv.getContext('2d');

        if (!ctx) return;

        ctx.drawImage(sourceImage, 0, 0, height, width);
        png.src = cnv.toDataURL(); // defaults to image/png
        resolve(png.src);
      }

      sourceImage.src = url;
    });
  }

  async exportPDF() {
      this.calculateMapSize();
      let visibleRisks = this.risks.filter(m => this.markerVisibility[m.id]);
      this.onExport.emit({ loading: true, current: 0, total: visibleRisks.length });

      // Hide the HTML overlay so it is not captured over the map screenshot
      this.capturing = true;
      await new Promise(res => setTimeout(res, 50));

      await this.getAttachments(visibleRisks.filter(m => !m.gallery));
      Object.keys(this.markers).forEach(k => this.markers[k].setMap(null));

      await this.pdfTool.exportRisksToPDF({
          risks: visibleRisks,
          project: this.project,
          startDate: this.startDate,
          endDate: this.endDate,
          onProgress: (current, total) => this.onExport.emit({ loading: true, current, total }),
          mapContext: {
              map: this.map,
              maps: this.maps,
              projection: this.projection,
              markers: this.markers,
              mapWidth: this.mapWidth,
              mapHeight: this.mapHeight,
              getMarkerIcon: (risk) => risk.iconHover
          }
      });

      Object.keys(this.markers).forEach(k => {
          if (this.markerVisibility[k]) this.markers[k].setMap(this.map.googleMap);
      });
      // Restore normal view: multi-risk pins stay hidden (overlay owns them), show the overlay again
      this.capturing = false;
      this.updateNativeMarkerVisibility();
      if (this.currentRisk) this.onMarkerClick(this.currentRisk);
      this.onExport.emit({ loading: false });
  }

  async getAttachment(attachmentId: string) {
    return new Promise(res => {
      let sub = this.attachmentsService.getAttachment(attachmentId).subscribe(att => {
        sub.unsubscribe();
        res(att);
      })
    })
  }
  async getAttachments(risks?: Risk[]) {
    return new Promise(res => {
      risks = risks ?? this.risks;
      if (!risks || !risks.length) return res([]);
      let ids = risks.filter(m => !m.gallery).map(m => m.payload.attachment);

      if (!ids.length) return res([]);
      this.attachmentsService.getAttachments({ ids }).pipe(takeUntil(this.destroy$)).subscribe(attachments => {
        risks!.forEach(metric => {
          let attach = attachments?.find(a => a.id == metric.payload.attachment);
          if (attach)
            metric.gallery = this.attachments[attach.id] = attach;
          res(attachments);
        });
      }, err => {
        this.pop.error(`An error occured while fetching images [status ${err.status}]`);
        res([]);
      })

    })
  }

  setMarkerVisibility(metricId: string, visible: boolean) {
    if (this.markers[metricId]) {
      this.markers[metricId].setVisible(visible);
    }

    if (this.markerVisibility[metricId] != undefined) {
      this.markerVisibility[metricId] = visible;
    }
  }

  rotationImage(angleDegrees: number) {
    const canvasContainer = document.createElement('canvas-container');
    const canvas = document.createElement('canvas');
    this.canvasElement = canvas;
    const ctx = canvas.getContext('2d');
    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      if (!ctx) return;

      let width = image.width;
      let height = image.height;
      const angle = angleDegrees * Math.PI / 180;

      const rotatedWidth = Math.abs(width * Math.cos(angle)) + Math.abs(height * Math.sin(angle));
      const rotatedHeight = Math.abs(width * Math.sin(angle)) + Math.abs(height * Math.cos(angle));

      canvas.width = rotatedWidth;
      canvas.height = rotatedHeight;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
      ctx.rotate(angle);
      ctx.drawImage(image, -width / 2, -height / 2, width, height);

      canvasContainer.style.width = rotatedWidth + 'px';
      canvasContainer.style.height = rotatedHeight + 'px';

      this.model.mapImage = canvas.toDataURL();
      this.model.origin.mapAngle = angleDegrees;
      this.imageAngle = angleDegrees;

      this.updateMap(this.model);
    }

    image.onerror = (error) => {
      console.log('Load image error :>> ', error);
    }

    image.src = this.model.mapImageSrc;
  }
}
